// src/components/StudentOnboarding.js
import React, { useState, useEffect } from "react";
import { useUpdateStudentMutation, useGetLeetcodeDataLazyQuery } from "../../../graphql/generated.tsx";
import { Button, Label, Progress, TextInput } from "flowbite-react";
import { IoMdArrowRoundBack } from "react-icons/io";
import { FaLongArrowAltRight } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { toast } from "../../../toast";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../../utils/firebase.js";

const StudentOnboarding = () => {
  const { id } = useParams();
  const [name, setName] = useState("");
  const [phno, setPhno] = useState("");
  const [dob, setDob] = useState("");
  const [placeOfResidence, setPlaceOfResidence] = useState("");
  const [password, setPassword] = useState("");
  const [leetCodeUname, setLeetCodeUname] = useState("");
  const [hackerRankUname, setHackerRankUname] = useState("");
  const [updateStudent, { loading, error }] = useUpdateStudentMutation();
  const [validateLeetCode, { loading: validatingLeetCode }] = useGetLeetcodeDataLazyQuery();
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const [email, setEmail] = useState();
  const [rollno, setRollNo] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [leetCodeError, setLeetCodeError] = useState("");
  const [department, setDepartment] = useState("");
  const [departmentError, setDepartmentError] = useState("");
  const [personalEmail, setPersonalEmail] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setEmail(user.email);
      }
    });
    return () => unsubscribe();
  }, []);

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhno(digitsOnly);
  };

  const handleDepartmentChange = (e) => {
    const value = e.target.value;
    // Only allow letters, numbers, and hyphens
    const validPattern = /^[a-zA-Z0-9-]*$/;
    if (value === "" || validPattern.test(value)) {
      setDepartment(value.toUpperCase());
      if (value.trim() !== "" && value.length > 0 && value.length < 2) {
        setDepartmentError("Minimum 2 characters required.");
      } else {
        setDepartmentError("");
      }
    } else if (/\s/.test(value)) {
      setDepartmentError("Spaces not allowed. Use '-' instead (eg: CSE, AIDS-A, MECH)");
    } else {
      setDepartmentError("Special characters not allowed. Only letters, numbers, and '-' are permitted.");
    }
  };

  const validatePassword = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1; // Minimum length
    if (/[A-Z]/.test(password)) strength += 1; // Uppercase letter
    if (/[a-z]/.test(password)) strength += 1; // Lowercase letter
    if (/\d/.test(password)) strength += 1; // Number
    if (/[\W_]/.test(password)) strength += 1; // Special character
    return strength;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(validatePassword(newPassword));
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return "Weak";
      case 2:
        return "Fair";
      case 3:
        return "Good";
      case 4:
      case 5:
        return "Strong";
      default:
        return "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLeetCodeError(""); // Clear previous errors

    if (!name || !rollno || !phno || !dob || !placeOfResidence || !leetCodeUname || !department || department.trim() === "" || !personalEmail) {
      if (!department || department.trim() === "") setDepartmentError("Department is required.");
      toast.error("Please fill in all required fields.");
      return;
    }

    if (department && department.trim() !== "" && department.length < 2) {
      setDepartmentError("Minimum 2 characters required.");
      toast.error("Department must be at least 2 characters.");
      return;
    }

    if (!validatePhoneNumber(phno)) {
      toast.error("Invalid phone number. Please enter a 10-digit phone number.");
      return;
    }

    if (!leetCodeUname || leetCodeUname.trim() === "") {
      setLeetCodeError("LeetCode Username is required.");
      return;
    }

    // Extract username first (in case they entered a URL)
    const extractedUsername = extractLeetCodeUsername(leetCodeUname);
    
    function extractLeetCodeUsername(input) {
      if (!input) return null;

      input = input.trim();

      // If user entered only username
      if (!input.includes("leetcode.com")) {
        return input;
      }

      try {
        const url = new URL(
          input.startsWith("http") ? input : `https://${input}`
        );

        // Possible formats:
        // /username/
        // /u/username
        const parts = url.pathname.split("/").filter(Boolean);

        if (parts[0] === "u" && parts[1]) {
          return parts[1];
        }

        if (parts[0]) {
          return parts[0];
        }

        return null;
      } catch (err) {
        return null;
      }
    }

    // Validate extracted username
    if (!extractedUsername) {
      setLeetCodeError("Could not extract a valid LeetCode username from the input.");
      return;
    }

    if (extractedUsername.length < 3) {
      setLeetCodeError("Enter valid user name ");
      return;
    }

    if (extractedUsername.length > 40) {
      setLeetCodeError("Enter valid user name ");
      return;
    }

    // Validate LeetCode username exists
    try {
      const { data: leetcodeData, error: leetcodeError } = await validateLeetCode({
        variables: { username: extractedUsername }
      });
      
      if (leetcodeError || !leetcodeData?.leetcodeData?.matchedUser?.username) {
        setLeetCodeError("LeetCode username not found. Please enter a valid username.");
        return;
      }
      
      setLeetCodeError(""); // Clear error on success
      toast.success("LeetCode username verified!");
    } catch (validationErr) {
      setLeetCodeError("Unable to verify LeetCode username. Please check and try again.");
      return;
    }

    try {
      await updateStudent({
        variables: {
          updateStudentInput: {
            id,
            email,
            rollNo: rollno,
            name,
            phno,
            dob,
            department,
            placeOfResidence,
            leetCodeUname: extractedUsername,
            hackerRankUname,
            personalEmail,
            password,
          },
        },
      });
      
      // Sign in and force token refresh to get updated flogin status
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await userCredential.user.reload();
      await userCredential.user.getIdToken(true); // Force refresh token
      
      toast.success("Onboarding completed successfully!");
      navigate("/");
      window.location.reload(); // Reload to update user state
    } catch (error) {
      toast.error("Error updating student.");
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-gray-100 font-poppins">
      <div className="relative w-full max-w-2xl p-6 bg-white rounded-lg shadow-md">
        <div className="absolute top-4 right-4">
          <img src={"/favicon.ico"} alt="Company Logo" className="h-16" />
        </div>

        {step === 1 && (
          <div className="p-4 text-center">
            <h2 className="mb-4 text-2xl font-bold text-gray-800">
              Welcome to Student Onboarding
            </h2>
            <p className="mb-8 text-gray-600">
              Please follow the instructions to complete your onboarding
              process.
            </p>
            <Button
              onClick={() => setStep(1.5)}
              className="w-full p-0 text-white rounded-md bg-primary"
            >
              Next Step
            </Button>
          </div>
        )}

        {step === 1.5 && (
          <div className="p-8">
            <div className="flex items-center mb-6">
              <button
                onClick={() => setStep(1)}
                className="mr-3 text-gray-500 transition-colors hover:text-gray-700 focus:outline-none"
              >
                <IoMdArrowRoundBack className="w-6 h-6" />
              </button>
              <h2 className="text-2xl font-semibold text-gray-800">
                Set Your Password
              </h2>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (passwordStrength < 3) {
                  toast.error("Password is too weak. Improve its strength.");
                  return;
                }
                if (password !== confirmPassword) {
                  toast.error("Passwords do not match. Please try again.");
                  return;
                }
                setStep(2);
              }}
              className="space-y-6"
            >
              <div>
                <Label
                  htmlFor="email"
                  className="text-sm font-medium text-gray-700"
                >
                  Email: <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  type="email"
                  value={email}
                  disabled
                  className="mt-1"
                />
              </div>
              <div>
                <div>
                  <Label
                    htmlFor="password"
                    className="flex items-center justify-between text-sm font-medium text-gray-700"
                  >
                    <span>
                      New Password: <span className="text-red-500">*</span>
                    </span>
                    <Progress
                      progress={(passwordStrength / 5) * 100}
                      color={
                        passwordStrength <= 2
                          ? "red"
                          : passwordStrength === 3
                          ? "yellow"
                          : "green"
                      }
                      size="sm"
                      className="w-[120px] rounded-full"
                    />
                  </Label>
                </div>
                <TextInput
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  className="mt-1"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Use at least 8 characters, including uppercase, lowercase,
                  numbers, and special characters.
                </p>
                <p
                  className={`mt-2 text-sm ${
                    passwordStrength <= 2
                      ? "text-red-500"
                      : passwordStrength === 3
                      ? "text-yellow-500"
                      : "text-green-500"
                  }`}
                >
                  {getPasswordStrengthText()}
                </p>
              </div>
              <div>
                <Label
                  htmlFor="confirmPassword"
                  className="text-sm font-medium text-gray-700"
                >
                  Confirm Password: <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="mt-1"
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="mt-2 text-sm text-red-500">
                    Passwords do not match.
                  </p>
                )}
              </div>
              <Button
                type="submit"
                className="w-full text-white transition-all rounded-md bg-primary hover:bg-primary-dark"
                disabled={
                  password !== confirmPassword ||
                  password === "" ||
                  confirmPassword === "" ||
                  passwordStrength < 3
                }
              >
                Proceed to Profile Setup
              </Button>
            </form>
          </div>
        )}

        {step === 2 && (
          <div className="p-8">
            <div className="flex items-center mb-4">
              <button
                onClick={() => setStep(1.5)}
                className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <IoMdArrowRoundBack className="w-6 h-6" />
              </button>
              <h2 className="text-2xl font-bold text-gray-800">
                Complete Your Profile
              </h2>
            </div>
            <form
              onSubmit={handleSubmit}
              className="space-y-6 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0"
            >
              <div>
                <Label htmlFor="name">
                  Name: <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="rollNo">
                  Roll No: <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  type="text"
                  value={rollno}
                  onChange={(e) => setRollNo(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phno">
                  Phone Number: <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  type="text"
                  value={phno}
                  onChange={handlePhoneChange}
                  inputMode="numeric"
                  maxLength={10}
                  required
                />
              </div>
              <div>
                <Label htmlFor="personalEmail">
                  Personal Mail: <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  type="email"
                  value={personalEmail}
                  onChange={(e) => setPersonalEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="dob">
                  Date of Birth: <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="department">
                  Department: <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  type="text"
                  value={department}
                  onChange={handleDepartmentChange}
                  placeholder="eg: CSE, AIDS-A, MECH"
                  color={departmentError ? "failure" : undefined}
                  helperText={departmentError && <span className="text-red-500 text-xs">{departmentError}</span>}
                />
              </div>
              <div>
                <Label htmlFor="hackerRankUname">HackerRank Username:</Label>
                <TextInput
                  type="text"
                  value={hackerRankUname}
                  onChange={(e) => setHackerRankUname(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="leetCodeUname">
                  LeetCode Username or Profile link: <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  type="text"
                  value={leetCodeUname}
                  onChange={(e) => {
                    setLeetCodeUname(e.target.value);
                    setLeetCodeError(""); // Clear error on input change
                  }}
                />
                {leetCodeError && (
                  <p className="mt-2 text-sm text-red-500">
                    {leetCodeError}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="placeOfResidence">
                  Place of Residence: <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  type="text"
                  value={placeOfResidence}
                  onChange={(e) => setPlaceOfResidence(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full p-0 text-white rounded-md lg:col-span-2 bg-primary"
                disabled={loading || validatingLeetCode}
              >
                <div className="flex items-center justify-center gap-2">
                  {validatingLeetCode ? "Validating LeetCode..." : loading ? "Submitting..." : "Let's Start Buddy!"}
                  {!validatingLeetCode && !loading && <FaLongArrowAltRight className="w-4 h-4 " />}
                </div>
              </Button>
              {(loading || validatingLeetCode) && (
                <p className="mt-2 text-center text-blue-500 lg:col-span-2">
                  {validatingLeetCode ? "Validating username..." : "Saving profile..."}
                </p>
              )}
              {error && (
                <p className="mt-2 text-center text-red-500 lg:col-span-2">
                  Error: {error.message}
                </p>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentOnboarding;